import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertTriangle, Clock, User, Calendar, CheckCircle2, X, Info } from "lucide-react";
import type { AuditRecord, Pathway, AmendmentReason } from "@/lib/pharmacyFirst";
import { DEFAULT_AMENDMENT_REASON_LABELS } from "@/lib/pharmacyFirst";
import { format } from "date-fns";

const AMENDMENT_FIELD_LABELS: Record<string, string> = {
  ageYears: "Age (years)",
  dateOfBirth: "Date of Birth",
  isFemale: "Sex",
  isPregnant: "Pregnant",
  isBreastfeeding: "Breastfeeding",
  weightKg: "Weight (kg)",
  heightCm: "Height (cm)",
  bmi: "BMI",
  hasPenicillinAllergy: "Penicillin Allergy",
  knownAllergies: "Known Allergies",
  currentMedications: "Current Medications",
  patientId: "Patient ID",
  biologicalSex: "Biological Sex",
  ethnicAdjustmentGroup: "Ethnicity",
  waistCircumferenceCm: "Waist Circumference (cm)",
};

function formatAmendmentField(field: string): string {
  if (AMENDMENT_FIELD_LABELS[field]) return AMENDMENT_FIELD_LABELS[field];
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

function formatAmendmentValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value || "—";
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((v: any) => (typeof v === "object" && v?.substance ? v.substance : String(v)))
      .join(", ");
  }
  return String(value);
}

interface ConsultationSummaryProps {
  audit: AuditRecord;
  pathway: Pathway;
  isDarkMode?: boolean;
  onClose?: () => void;
  readOnly?: boolean;
  overrideStartedAt?: string;
  overrideTotalAnswers?: number;
  hideDuration?: boolean;
}

function formatGatewayId(gatewayId: string): string {
  // Convert GATEWAY_DIFFERENTIAL_CLEARED -> Differential screen cleared
  const label = gatewayId
    .replace(/^GATEWAY_/, "")
    .replace(/_/g, " ")
    .toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function ConsultationSummary({
  audit,
  pathway,
  isDarkMode = false,
  onClose,
  readOnly = false,
  overrideStartedAt,
  overrideTotalAnswers,
  hideDuration = false,
}: ConsultationSummaryProps) {
  // Resolve pathway-level clinical links (prefer clinicalNoteLinkRefs)
  const headerLinks = React.useMemo(() => {
    if (!pathway) return [] as any[];
    const linksMap = (pathway.links as any) || {};
    const refs: string[] = (pathway as any).clinicalNoteLinkRefs || [];
    const resolved: any[] = [];
    for (const r of refs) {
      const l = linksMap[r];
      if (l) resolved.push(l);
    }
    if (resolved.length === 0) {
      // Fallback: pick any clinician-facing web links
      for (const k of Object.keys(linksMap)) {
        const l = linksMap[k];
        if (l && l.audience === "clinician") resolved.push(l);
      }
    }
    return resolved;
  }, [pathway]);
  const outcome = audit.finalOutcome;
  const isSupply = outcome?.outcomeType === "supply";
  const isReferral = outcome?.outcomeType?.includes("refer");
  const isEmergency = outcome?.outcomeType === "emergency";
  const isSelfCare = outcome?.outcomeType === "self_care";

  const getOutcomeIcon = () => {
    if (isEmergency) return <AlertTriangle className="h-12 w-12 text-red-600" />;
    if (isSupply) return <CheckCircle className="h-12 w-12 text-green-600" />;
    if (isReferral) return <AlertTriangle className="h-12 w-12 text-yellow-600" />;
    if (outcome?.outcomeType === "cannot_supply") return <X className="h-12 w-12 text-red-600" />;
    return <CheckCircle className="h-12 w-12 text-blue-600" />;
  };

  const getOutcomeColor = () => {
    if (isEmergency) return "bg-red-50 border-red-200";
    if (isSupply) return "bg-green-50 border-green-200";
    if (isReferral) return "bg-yellow-50 border-yellow-200";
    if (outcome?.outcomeType === "cannot_supply") return "bg-red-50 border-red-200";
    return "bg-blue-50 border-blue-200";
  };

  const effectiveStartedAt = overrideStartedAt || audit.startedAt;
  const consultationDuration =
    audit.completedAt && effectiveStartedAt
      ? new Date(audit.completedAt).getTime() -
      new Date(effectiveStartedAt).getTime()
      : 0;
  const durationMinutes = Math.round(consultationDuration / 1000 / 60);
  const totalQuestionsAnswered = overrideTotalAnswers ?? audit.answers.length;

  return (
    <div
      className={`flex flex-col h-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
        } overflow-y-auto`}
    >
      {/* Outcome card */}
      <div
        className={`border rounded-2xl p-8 m-6 space-y-4 ${getOutcomeColor()} border-2`}
      >
        <div className="flex items-center justify-center mb-4">
          {getOutcomeIcon()}
        </div>
        {!isSupply && (
          <>
            <h2
              className={`text-2xl font-bold text-center ${isEmergency
                  ? "text-red-900"
                  : isReferral
                    ? "text-yellow-900"
                    : outcome?.outcomeType === "cannot_supply"
                      ? "text-red-900"
                      : "text-blue-900"
                }`}
            >
              {(outcome as any)?.title ||
                (outcome?.outcomeType === "refer_urgent"
                  ? "Urgent Referral"
                  : outcome?.outcomeType === "refer_routine"
                    ? "Routine Referral"
                    : outcome?.outcomeType === "self_care"
                      ? "Self-Care Advice"
                      : outcome?.outcomeType === "emergency"
                        ? "Emergency"
                        : outcome?.outcomeType === "cannot_supply"
                          ? "Cannot Supply"
                          : "Consultation Complete")}
            </h2>
            {outcome?.reason && (
              <p
                className={`text-center text-sm ${isEmergency
                    ? "text-red-800"
                    : isReferral
                      ? "text-yellow-800"
                      : outcome?.outcomeType === "cannot_supply"
                        ? "text-red-800"
                        : "text-blue-800"
                  }`}
              >
                {outcome.reason}
              </p>
            )}
          </>
        )}
      </div>

      {/* Summary details */}
      <div className={`m-6 space-y-4`}>
        {/* Header links (NICE CKS etc.) */}
        {headerLinks.length > 0 && (
          <div className={`mb-2 flex flex-wrap gap-2`}>
            {headerLinks.map((l: any, i: number) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer">
                <Badge variant="outline" className={isDarkMode ? "border-blue-700 bg-blue-950 text-blue-200 items-center gap-1 inline-flex" : "border-blue-200 bg-blue-50 text-blue-700 items-center gap-1 inline-flex"}>
                  <Info className="h-3 w-3" />
                  <span className="ml-1">{l.label}</span>
                </Badge>
              </a>
            ))}
          </div>
        )}

        {/* Patient info */}
        <div
          className={`border rounded-lg p-4 ${isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
            }`}
        >
          <h3
            className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            <User className="h-4 w-4" />
            Patient Information
          </h3>
          <dl className="space-y-2 text-sm">
            {(audit.patient as any)?.nhsNumber && (
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  NHS Number:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {(audit.patient as any).nhsNumber}
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt
                className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Date of Birth:
              </dt>
              <dd
                className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
              >
                {audit.patient?.dateOfBirth || "—"}
              </dd>
            </div>
            {audit.patient?.isFemale !== undefined && (
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Sex:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.patient?.isFemale ? "Female" : "Male"}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Consultation info */}
        <div
          className={`border rounded-lg p-4 ${isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
            }`}
        >
          <h3
            className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            <Calendar className="h-4 w-4" />
            Consultation Details
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt
                className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Pathway:
              </dt>
              <dd
                className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
              >
                {pathway.name}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt
                className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Started:
              </dt>
              <dd
                className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
              >
                {format(new Date(effectiveStartedAt), "HH:mm:ss")}
              </dd>
            </div>
            {!hideDuration && (
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Duration:
                </dt>
                <dd
                  className={`font-medium flex items-center gap-1 ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  <Clock className="h-3 w-3" />
                  {durationMinutes} minutes
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt
                className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Questions Answered:
              </dt>
              <dd
                className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
              >
                {totalQuestionsAnswered}
              </dd>
            </div>
          </dl>
        </div>

        {/* Amendments summary */}
        {audit.amendments.length > 0 && (
          <div
            className={`border rounded-lg p-4 ${isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
              }`}
          >
            <h3
              className={`font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              Patient Information Amendments
            </h3>
            <ul className="space-y-2 text-sm">
              {audit.amendments.map((amendment, idx) => (
                <li
                  key={idx}
                  className={`p-2 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-50"
                    }`}
                >
                  <p
                    className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                  >
                    {formatAmendmentField(amendment.field)}
                  </p>
                  <p
                    className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                  >
                    {formatAmendmentValue(amendment.originalValue)} → {formatAmendmentValue(amendment.newValue)}
                  </p>
                  <p
                    className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                  >
                    Reason: {DEFAULT_AMENDMENT_REASON_LABELS[amendment.reason as AmendmentReason] || amendment.reason}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Payment info */}
        {audit.paymentTriggered && (
          <div
            className={`border rounded-lg p-4 ${isDarkMode
                ? "border-emerald-700 bg-emerald-900/20"
                : "border-emerald-200 bg-emerald-50"
              }`}
          >
            <h3
              className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-emerald-400" : "text-emerald-700"
                }`}
            >
              <CheckCircle2 className="h-4 w-4" />
              Payment Claimable
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Claim Trigger:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.paymentTriggeredBy
                    ? formatGatewayId(audit.paymentTriggeredBy)
                    : "—"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Time:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.paymentTriggeredAt
                    ? format(new Date(audit.paymentTriggeredAt), "HH:mm:ss")
                    : "—"}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* Supply details */}
        {audit.supplyDetails && (
          <div
            className={`border rounded-lg p-4 ${isDarkMode
                ? "border-green-700 bg-green-900/20"
                : "border-green-200 bg-green-50"
              }`}
          >
            <h3
              className={`font-semibold mb-3 text-green-700 ${isDarkMode ? "text-green-400" : ""
                }`}
            >
              PGD Supply Details
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Drug:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.supplyDetails.drugId.charAt(0).toUpperCase() +
                    audit.supplyDetails.drugId.slice(1)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Dose:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {/* Combination products resolve to a ready-made label
                      ("8mg/90mg × 1 morning"), so only a bare number needs the
                      unit appending. */}
                  {typeof audit.supplyDetails.resolvedDoseMg === "number"
                    ? `${audit.supplyDetails.resolvedDoseMg}mg`
                    : audit.supplyDetails.resolvedDoseMg}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Quantity:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.supplyDetails.quantity.label}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt
                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Duration:
                </dt>
                <dd
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                >
                  {audit.supplyDetails.totalDurationDays} days
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="m-6 space-y-2 mt-auto">
        {readOnly ? (
          <div className={`flex items-center justify-center gap-2 p-3 rounded-lg ${isDarkMode ? "bg-green-900/30 border border-green-700" : "bg-green-50 border border-green-200"}`}>
            <CheckCircle className={`h-5 w-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
            <span className={`font-semibold text-sm ${isDarkMode ? "text-green-300" : "text-green-700"}`}>
              Consultation Completed
            </span>
          </div>
        ) : (
          <Button onClick={onClose} size="lg" className="w-full">
            Complete Consultation
          </Button>
        )}
      </div>
    </div>
  );
}
